import { AREAS } from './site';
import expertiseImg from '../assets/expertise.webp';
import rapportImg from '../assets/rapport_couverture.webp';

const PARENT = '/expertise-assurance';

export const assurance = [
  {
    path: '/expertise-assurance',
    nav: 'Expertise assurance',
    summary: 'Expertise de sinistres pour les compagnies et contre-expertise pour les assurés.',
    serviceType: "Expertise d'assurance",
    areaServed: AREAS.lu,
    title: "Expertise sinistre et assurance au Luxembourg | Expomis",
    description: 'Expertise de sinistres au Luxembourg : incendie, heurt, vol, tempête, dégâts des eaux, responsabilité civile. Pour les assureurs et en contre-expertise.',
    eyebrow: 'Expertise assurance',
    h1: "Expertise d'assurance au Luxembourg",
    image: expertiseImg,
    imageAlt: 'Sinistres expertisés : incendie, dégât des eaux, tempête, vol',
    intro: [
      "Expomis réalise des expertises de sinistres pour le compte de compagnies d'assurance, et intervient aussi comme [contre-expert indépendant](/contre-expertise) au service des assurés. Nous établissons les causes du sinistre, évaluons les dommages et documentons le dossier, avec plus de 20 ans d'expérience.",
    ],
    sections: [
      {
        h2: 'Les sinistres que nous expertisons',
        list: [
          "Incendie et heurt : analyse des dégâts causés par un incendie ou un heurt, et évaluation des dommages.",
          "Vol : évaluation des pertes et dégâts matériels après un cambriolage ou une tentative d'effraction.",
          'Tempête : dommages causés par les intempéries, vents violents, grêle, inondations.',
          "[Dégâts des eaux](/degat-des-eaux) : identification de la cause et estimation des réparations. C'est là que notre activité de [recherche de fuite](/recherche-fuite-luxembourg) fait la différence.",
          'Responsabilité civile : analyse des réclamations pour établir les responsabilités et leurs conséquences financières.',
        ],
      },
      {
        h2: 'Deux rôles, une même rigueur',
        body: [
          "Mandatés par une compagnie, nous établissons les faits et évaluons les dommages de façon objective. Aux côtés d'un assuré, en contre-expertise, nous vérifions que son sinistre est évalué à sa juste valeur. Dans les deux cas, le travail repose sur les mêmes constats, les mêmes mesures et un rapport documenté.",
        ],
      },
      {
        h2: 'Notre approche',
        ordered: true,
        list: [
          'Analyse : diagnostic de la situation et des causes du sinistre.',
          'Documentation : photos, mesures, relevés.',
          'Évaluation : estimation des dommages et des réparations.',
          'Rapport : compte rendu complet sous 72h.',
        ],
      },
    ],
    childrenTitle: 'Pour aller plus loin',
    children: ['/degat-des-eaux', '/fuite-eau-qui-paie', '/contre-expertise', '/rapport-recherche-fuite'],
  },
  {
    path: '/degat-des-eaux',
    parent: PARENT,
    nav: 'Dégât des eaux',
    summary: "Trouver l'origine de l'eau et documenter le sinistre pour l'assurance.",
    serviceType: 'Expertise dégât des eaux',
    areaServed: AREAS.lu,
    title: 'Dégât des eaux au Luxembourg : cause et assurance | Expomis',
    description: "Après un dégât des eaux : premiers réflexes, recherche de la cause, évaluation des dommages, dossier d'assurance. Recherche de fuite et expertise.",
    eyebrow: 'Expertise assurance',
    h1: 'Dégât des eaux : trouver la cause et documenter le sinistre',
    intro: [
      "Après un dégât des eaux, deux questions se posent : d'où vient l'eau, et qui prend en charge les dommages. Expomis répond aux deux, parce que nous faisons à la fois la [recherche de fuite](/recherche-fuite-luxembourg) et l'[expertise de sinistre](/expertise-assurance).",
    ],
    sections: [
      {
        h2: 'Les premiers réflexes',
        ordered: true,
        list: [
          "Stoppez l'arrivée d'eau : fermez la vanne générale, ou celle de l'appareil en cause.",
          "Coupez l'électricité des pièces touchées si l'eau approche des prises ou des appareils.",
          'Photographiez les dégâts avant de déplacer ou de nettoyer quoi que ce soit.',
          'Prévenez votre assureur dans les délais prévus par votre contrat, et votre voisin ou votre syndic si d’autres logements sont concernés.',
          "Conservez les biens endommagés jusqu'à l'expertise.",
        ],
      },
      {
        h2: 'Pourquoi la cause compte autant',
        body: [
          "La prise en charge dépend souvent de l'origine de l'eau : canalisation privative, partie commune de l'immeuble, logement voisin, infiltration par la toiture, refoulement d'égout. Chaque cas peut relever d'un contrat ou d'un responsable différent. Tant que la cause n'est pas établie précisément, le dossier peut rester bloqué. Notre guide [Fuite d'eau au Luxembourg : qui paie ?](/fuite-eau-qui-paie) détaille chaque situation.",
          'C’est pourquoi nous commençons par localiser la fuite, avec des techniques non destructives, avant d’évaluer les dommages.',
        ],
      },
      {
        h2: 'Ce que nous faisons',
        list: [
          "localisation de la fuite ou de l'[infiltration](/recherche-infiltration) ;",
          "constat des dommages : photos, [mesures d'humidité](/techniques/humidimetre), étendue de la zone touchée ;",
          '[rapport](/rapport-recherche-fuite) sous 72h, transmissible à votre assureur ;',
          "expertise du sinistre, pour le compte d'une compagnie ou en [contre-expertise](/contre-expertise) à vos côtés.",
        ],
      },
    ],
    faq: [
      {
        q: 'Qui paie la recherche de fuite ?',
        a: "Dans de nombreux cas, l'assurance habitation prend en charge les frais de recherche de fuite lorsqu'elle a causé un dégât des eaux. Les conditions et plafonds varient : vérifiez votre contrat. Locataire, copropriété, voisin : voir notre guide [Fuite d'eau : qui paie ?](/fuite-eau-qui-paie).",
      },
      {
        q: "Dois-je attendre l'expert avant de réparer ?",
        a: "Prenez tout de suite les mesures urgentes pour arrêter la fuite et limiter les dégâts : elles sont attendues de vous. Pour les réparations définitives, prévenez d'abord votre assureur et conservez des photos de l'état avant travaux.",
      },
    ],
    related: ['/fuite-eau-qui-paie', '/recherche-fuite-luxembourg', '/contre-expertise'],
  },
  {
    path: '/fuite-eau-qui-paie',
    parent: '/degat-des-eaux',
    guide: { datePublished: '2026-10-14' },
    nav: "Fuite d'eau : qui paie ?",
    summary: 'Locataire, propriétaire, copropriété, voisin, réseau public : qui prend en charge la fuite et ses dégâts.',
    areaServed: AREAS.lu,
    title: "Fuite d'eau au Luxembourg : qui paie ? Guide complet | Expomis",
    description: "Fuite d'eau au Luxembourg : qui paie la réparation, les dégâts et la recherche de fuite ? Locataire, propriétaire, copropriété, voisin, compteur, logement neuf.",
    eyebrow: 'Guide — octobre 2026',
    h1: "Fuite d'eau au Luxembourg : qui paie ?",
    intro: [
      "Après une fuite d'eau, trois dépenses se présentent : la recherche de la fuite, la réparation de ce qui fuit, et la remise en état des dégâts causés par l'eau. Elles ne sont pas forcément à la charge de la même personne. Qui paie dépend surtout d'un fait : où se trouve exactement la fuite.",
      "Ce guide présente les règles générales que nous rencontrons en tant que [chercheurs de fuite](/recherche-fuite-luxembourg) et experts en sinistres au Luxembourg. Ce n'est pas un avis juridique : votre contrat d'assurance, votre bail et le règlement de copropriété font foi. En cas de litige, adressez-vous à votre assureur, à votre protection juridique ou à un avocat.",
    ],
    sections: [
      {
        h2: 'En bref',
        list: [
          "Maison dont vous êtes propriétaire occupant : vous payez la réparation ; votre assurance habitation couvre en général les dégâts des eaux et souvent les frais de recherche de fuite.",
          "Logement loué : les petites réparations d'entretien sont en principe à la charge du locataire, les réparations des canalisations et de l'installation à celle du propriétaire, sauf faute ou défaut d'entretien du locataire.",
          "Copropriété : une fuite sur une partie commune relève de la copropriété, représentée par le syndic ; une fuite sur une partie privative relève du copropriétaire concerné.",
          "Fuite venant du voisin : vos dégâts sont en général indemnisés par votre propre assurance, qui peut ensuite se retourner contre le responsable.",
          "Fuite avant le compteur d'eau : elle relève en général du réseau public (commune ou syndicat des eaux) ; après le compteur, du propriétaire.",
          "Logement récent : la fuite peut relever des garanties du promoteur ou des entreprises.",
        ],
        after: [
          "Dans tous les cas, c'est la localisation précise de la fuite qui permet de savoir dans quelle situation on se trouve.",
        ],
      },
      {
        h2: "Pourquoi tout dépend de l'endroit de la fuite",
        body: [
          "Une même tache au plafond peut venir d'une canalisation privative, d'une colonne commune de l'immeuble, de la douche du voisin, de la toiture ou d'un refoulement d'égout. Chacune de ces origines désigne un responsable et un contrat d'assurance différents. Tant que l'origine n'est pas établie, chacun peut renvoyer la charge sur l'autre, et le dossier reste bloqué.",
          "C'est pourquoi un [rapport de recherche de fuite](/rapport-recherche-fuite) qui situe l'origine, photos et mesures à l'appui, est souvent la première pièce demandée par les assureurs.",
        ],
      },
      {
        h2: 'Vous êtes propriétaire de votre maison',
        body: [
          "La réparation de l'installation (canalisation, raccord, chauffage, évacuation) est à votre charge : c'est votre bien. Les dommages causés par l'eau (plafonds, murs, sols, mobilier) sont en général couverts par votre assurance habitation au titre du dégât des eaux, selon les conditions de votre contrat.",
          "Beaucoup de contrats prennent aussi en charge les frais de recherche de fuite lorsqu'une fuite a causé un dégât des eaux, souvent dans la limite d'un plafond. Ils couvrent plus rarement la réparation de la canalisation elle-même, et rarement l'eau perdue.",
        ],
      },
      {
        h2: 'Vous êtes locataire, ou propriétaire bailleur',
        body: [
          "Le principe : le locataire assure l'entretien courant et les menues réparations (joints, flexibles, robinetterie, débouchage des siphons), le propriétaire les réparations plus importantes de l'installation (canalisations encastrées, chauffage, évacuations). Une fuite sur une canalisation encastrée relève donc en général du propriétaire, sauf si elle résulte d'une faute ou d'un défaut d'entretien du locataire.",
          "Le locataire doit prévenir le propriétaire sans tarder : laisser une fuite s'aggraver sans rien dire peut engager sa responsabilité. Le bail exige souvent que le locataire soit assuré pour les risques locatifs ; son assurance peut intervenir pour ses biens et sa responsabilité. L'assurance du propriétaire couvre en général le bâtiment.",
          "Qui commande la recherche de fuite ? Le locataire, le propriétaire ou le syndic peuvent le faire. Le plus simple est de se mettre d'accord avant : le rapport, qui situe l'origine de la fuite, sert ensuite à répartir les frais.",
        ],
      },
      {
        h2: 'Vous êtes en copropriété',
        body: [
          "Le règlement de copropriété distingue les parties privatives, qui appartiennent à chaque copropriétaire, et les parties communes, qui appartiennent à tous. En règle générale, les colonnes montantes d'eau et d'évacuation, la toiture et la façade sont des parties communes, tandis que les canalisations qui desservent un seul logement, après sa vanne d'arrêt, sont privatives. Mais c'est le règlement de votre immeuble qui fixe la limite.",
          "Une fuite sur une partie commune est à la charge de la copropriété : prévenez le syndic, qui fait intervenir l'assurance de l'immeuble. Une fuite sur une partie privative est à la charge du copropriétaire concerné et de son assurance. Voir aussi notre page [Luxembourg-Ville](/recherche-fuite-luxembourg-ville), où ce cas est fréquent.",
        ],
      },
      {
        h2: "L'eau vient de chez le voisin",
        body: [
          "Déclarez le sinistre à votre propre assureur : c'est en général lui qui vous indemnise selon votre contrat, puis se retourne, le cas échéant, contre le responsable ou son assureur. Prévenez aussi le voisin et, en copropriété, le syndic.",
          "Pour localiser la fuite, il faudra accéder au logement d'où elle provient. Si le voisin conteste, le rapport de recherche de fuite établit les faits.",
        ],
      },
      {
        h2: 'Avant ou après le compteur',
        body: [
          "En règle générale, le branchement jusqu'au compteur relève du réseau public (commune ou syndicat des eaux), et tout ce qui se trouve après le compteur relève du propriétaire. La limite exacte et les règles de prise en charge sont fixées par le règlement de votre commune : renseignez-vous auprès de son service des eaux.",
          "En cas de fuite cachée après le compteur, certaines communes accordent une réduction de la facture d'eau une fois la fuite localisée et réparée. Voir notre page [surconsommation d'eau](/surconsommation-eau).",
        ],
      },
      {
        h2: 'Logement neuf ou récemment rénové',
        body: [
          "Si le logement est récent, la fuite peut relever des garanties dues par le promoteur ou les entreprises qui ont construit ou rénové : garantie biennale ou décennale selon l'élément en cause. Prévenez-les par écrit, et faites documenter l'origine de la fuite avant qu'elle ne soit réparée, pour ne pas perdre les preuves. Voir notre page [Differdange](/recherche-fuite-differdange), où ce cas est détaillé.",
        ],
      },
      {
        h2: "Infiltration et refoulement d'égout",
        body: [
          "Une [infiltration](/recherche-infiltration) par la toiture, une terrasse ou la façade relève en général du propriétaire du bâtiment ou, en copropriété, de la copropriété. Un refoulement d'égout peut relever du réseau public ou de l'installation privée, selon son origine. Les contrats d'assurance traitent ces cas de façons très diverses, et certains les excluent : vérifiez vos conditions.",
        ],
      },
      {
        h2: "Si vous n'êtes pas d'accord avec l'indemnisation",
        body: [
          "Si l'évaluation de l'expert de votre assureur vous paraît sous-estimée, ou si la cause retenue vous semble erronée, vous pouvez faire appel à votre propre expert. C'est la [contre-expertise](/contre-expertise), prévue par la plupart des contrats.",
        ],
      },
    ],
    faq: [
      {
        q: 'Qui paie la recherche de fuite ?',
        a: "Celui qui la commande l'avance en général. Elle est souvent remboursée par l'assurance habitation lorsque la fuite a causé un dégât des eaux, dans la limite d'un plafond. En location ou en copropriété, les frais peuvent ensuite être répartis selon l'origine établie par le rapport.",
      },
      {
        q: "Mon assurance rembourse-t-elle l'eau perdue ?",
        a: "Rarement. Les contrats couvrent surtout les dommages causés par l'eau. En revanche, certaines communes accordent une réduction de la facture après une fuite cachée localisée et réparée.",
      },
      {
        q: 'Je suis locataire : dois-je payer la réparation de la fuite ?',
        a: "Pas si elle porte sur une canalisation encastrée ou un élément important de l'installation, sauf faute ou défaut d'entretien de votre part. Les menues réparations (joints, flexibles, robinets) sont en principe à votre charge. Votre bail peut préciser la répartition.",
      },
      {
        q: 'Le voisin du dessus refuse de laisser entrer pour chercher la fuite. Que faire ?',
        a: "Prévenez votre assureur et, en copropriété, le syndic, qui peuvent intervenir auprès de lui. Documentez les dégâts chez vous par des photos et des mesures. Si le blocage persiste, demandez conseil à votre assureur ou à un avocat.",
      },
      {
        q: 'Faut-il attendre le passage de l’expert pour réparer ?',
        a: "Prenez tout de suite les mesures urgentes pour arrêter la fuite et limiter les dégâts. Pour les réparations définitives, prévenez d'abord votre assureur et conservez des photos de l'état avant travaux.",
      },
    ],
    related: ['/degat-des-eaux', '/rapport-recherche-fuite', '/contre-expertise'],
  },
  {
    path: '/contre-expertise',
    parent: PARENT,
    nav: 'Contre-expertise',
    summary: "Un expert indépendant à vos côtés face à l'expert de votre assureur.",
    serviceType: "Contre-expertise d'assurance",
    areaServed: AREAS.lu,
    title: "Contre-expertise d'assurance au Luxembourg | Expomis",
    description: "Indemnité trop faible, cause contestée ? Un contre-expert indépendant défend vos intérêts face à l'expert de l'assureur. Frais souvent couverts par le contrat.",
    eyebrow: 'Expertise assurance',
    h1: 'Contre-expertise : un expert indépendant à vos côtés',
    intro: [
      "Après un sinistre, votre assureur mandate un expert pour évaluer les dommages. Si vous estimez que son évaluation ne reflète pas la réalité (montant sous-estimé, cause contestée, dommages oubliés), vous pouvez faire appel à votre propre expert. Expomis intervient comme contre-expert indépendant, à vos côtés face à l'expert de la compagnie.",
    ],
    sections: [
      {
        h2: 'Un droit, pas un conflit',
        body: [
          "Faire appel à un contre-expert est un droit prévu par votre contrat, pas un signe de conflit avec votre assureur. Les deux experts examinent le sinistre ensemble et cherchent à s'accorder sur les causes et le montant des dommages.",
        ],
      },
      {
        h2: 'Quand demander une contre-expertise ?',
        list: [
          "l'indemnité proposée vous paraît trop faible au regard des dommages ;",
          "la cause du sinistre est contestée, et la garantie refusée sur cette base ;",
          "des dommages n'ont pas été pris en compte ;",
          "le sinistre est important ou complexe : incendie, dégât des eaux étendu ;",
          "vous ne vous sentez pas en mesure de discuter les aspects techniques avec l'expert de la compagnie.",
        ],
      },
      {
        h2: 'Comment se déroule une contre-expertise',
        ordered: true,
        list: [
          "Nous analysons votre dossier : contrat, rapport de l'expert de la compagnie, photos, factures.",
          "Nous examinons le sinistre sur place, avec nos propres constats et mesures. Pour un dégât des eaux, nous pouvons [localiser nous-mêmes l'origine de la fuite](/recherche-fuite-luxembourg).",
          'Nous chiffrons les dommages.',
          "Nous rencontrons l'expert de la compagnie pour confronter les évaluations et aboutir à un accord.",
        ],
        after: [
          "Si le désaccord persiste, de nombreux contrats prévoient le recours à un troisième expert pour trancher.",
        ],
      },
      {
        h2: 'Pourquoi Expomis',
        body: [
          "Nous réalisons aussi des expertises pour le compte de compagnies d'assurance. Nous connaissons leurs méthodes d'évaluation et les justificatifs qu'elles attendent, ce qui rend la discussion plus rapide et plus factuelle.",
        ],
      },
    ],
    faq: [
      {
        q: 'Faire appel à un contre-expert, est-ce un signe de conflit avec mon assureur ?',
        a: "Non, c'est un droit prévu par votre contrat d'assurance. Un contre-expert indépendant défend vos intérêts face à l'expert de la compagnie, sans confrontation.",
      },
      {
        q: 'Combien coûte une contre-expertise ?',
        a: "Les frais de contre-expertise sont souvent couverts, en tout ou partie, par votre contrat d'assurance (garantie protection juridique ou clause dédiée). Nous restons disponibles pour en discuter avec vous sans engagement.",
      },
      {
        q: 'À quel moment faut-il demander une contre-expertise ?',
        a: "Idéalement avant d'accepter la proposition d'indemnisation de votre assureur. Une fois l'accord signé, il est souvent difficile de revenir dessus.",
      },
    ],
    related: ['/degat-des-eaux', '/expertise-assurance', '/rapport-recherche-fuite'],
  },
  {
    path: '/rapport-recherche-fuite',
    parent: PARENT,
    nav: 'Le rapport de recherche de fuite',
    summary: 'Ce que contient notre rapport, remis sous 72h, et à qui il sert.',
    serviceType: 'Rapport de recherche de fuite',
    title: "Rapport de recherche de fuite pour l'assurance | Expomis",
    description: "Le rapport de recherche de fuite Expomis, remis sous 72h : constats, photos, mesures, techniques, origine de la fuite, dommages. Pour l'assureur et le syndic.",
    eyebrow: 'Expertise assurance',
    h1: 'Le rapport de recherche de fuite',
    image: rapportImg,
    imageAlt: "Page de couverture d'un rapport d'expertise Expomis",
    intro: [
      "À l'issue de chaque intervention, Expomis vous remet sous 72h un rapport complet, illustré de photos et de mesures. C'est le document qu'attendent votre assureur, votre syndic et l'entreprise qui réparera.",
    ],
    sections: [
      {
        h2: 'Ce que contient le rapport',
        list: [
          'le contexte : adresse, date, symptômes constatés ;',
          "les constats : photos, [mesures d'humidité](/techniques/humidimetre), étendue de la zone touchée ;",
          'les [techniques utilisées](/techniques) et leurs résultats ;',
          'la localisation de la fuite, avec photos du marquage sur place ;',
          "l'origine identifiée et le circuit concerné ;",
          'les dommages observés.',
        ],
      },
      {
        h2: 'À qui il sert',
        list: [
          "à votre assureur, pour instruire le [dossier de dégât des eaux](/degat-des-eaux) ;",
          'à votre syndic ou à la copropriété, pour établir si la fuite provient d’une partie privative ou commune ;',
          "à l'entreprise qui répare, pour intervenir directement au bon endroit ;",
          'à vous, propriétaire ou locataire, pour savoir qui est responsable de la réparation.',
        ],
      },
    ],
    faq: [
      {
        q: 'Sous quel délai recevons-nous le rapport ?',
        a: 'Vous recevez un rapport complet et détaillé, illustré de photos et de mesures, sous 72h maximum.',
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/degat-des-eaux', '/contre-expertise'],
  },
];
